import { BASKET_TYPES, isPhoneChangeAllowed, selectBasketContractGroups, selectBasketCustomer, selectBasketIsPaid, selectBasketList, selectBasketTerms, sendBasket, updateTerms } from 'containers/OrderDetails/basketSlice'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectUser } from 'containers/Login/loginSlice'
import { selectConfig } from 'appSlice'

/**
 * Sposób akceptacji umowy w podsumowaniu (gdy ekran podpisywania umowy jest wyłączony):
 * - 'none'      - ekran podpisu włączony albo brak najmu w koszyku
 * - 'signature' - niezweryfikowany telefon bez wynajętych magazynów (ten sam warunek co zmiana numeru
 *                 w SignAgreementPopup): sekcja "Umowa" + podpis kodem SMS z poziomu podsumowania
 * - 'checkbox'  - checkbox z akceptacją umowy (contract_accepted)
 */
export const CONTRACT_MODES = { none: 'none', checkbox: 'checkbox', signature: 'signature' }

const INITIAL_CONTRACT = { isLoading: false, isLoaded: false, isError: false }

const isFulfilled = res => res?.meta?.requestStatus === 'fulfilled'
const hasContractPdf = res => isFulfilled(res) && !!res?.payload?.contractgroups?.[0]?.pdf

/**
 * Wejście na podsumowanie: step "checkout" (walidacja), a w wariancie bez ekranu podpisywania
 * umowy zaraz po nim step "contract", który generuje umowę do podglądu.
 * Przejście dalej jest możliwe dopiero po pobraniu obu.
 */
const useCheckout = () => {
  const contractGroups = useSelector(selectBasketContractGroups)
  const customer = useSelector(selectBasketCustomer)
  const isPaid = useSelector(selectBasketIsPaid)
  const terms = useSelector(selectBasketTerms)
  const list = useSelector(selectBasketList)
  const config = useSelector(selectConfig)
  const user = useSelector(selectUser)
  const dispatch = useDispatch()

  const isContractVariant = !config?.contractSigningScreenEnabled && list?.some(item => item.type === BASKET_TYPES.rent)
  const needsContract = isContractVariant && !isPaid
  const mode = !isContractVariant
    ? CONTRACT_MODES.none
    : isPhoneChangeAllowed(customer, user)
      ? CONTRACT_MODES.signature
      : CONTRACT_MODES.checkbox

  const [isLoading, setLoading] = useState(!isPaid)
  const [contractState, setContractState] = useState({ ...INITIAL_CONTRACT, isLoading: needsContract })
  const isActive = useRef(true)
  const initData = useRef({ needsContract, terms })

  const requestContract = useCallback(async () => {
    const res = await dispatch(sendBasket({ step: 'contract' }))
    const isOk = hasContractPdf(res)
    if (isActive.current) {
      setContractState({ isLoading: false, isLoaded: isOk, isError: !isOk })
    }
    return isOk
  }, [dispatch])

  const loadContract = useCallback(() => {
    setContractState(prev => ({ ...prev, isLoading: true, isError: false }))
    return requestContract()
  }, [requestContract])

  useEffect(() => {
    isActive.current = true
    if (isPaid) return

    // Umowa generuje się od nowa przy każdym wejściu, więc wcześniejsza zgoda przestaje jej dotyczyć.
    // Reset przed wysłaniem checkoutu, żeby nie poleciało już contract_accepted: true.
    if (initData.current.needsContract && initData.current.terms?.rentalTerms) {
      dispatch(updateTerms({ ...initData.current.terms, rentalTerms: false }))
    }

    dispatch(sendBasket({ step: 'checkout' })).then(res => {
      if (!isActive.current) return
      setLoading(false)
      if (!initData.current.needsContract) return
      if (isFulfilled(res)) {
        loadContract()
      } else {
        setContractState({ ...INITIAL_CONTRACT, isError: true })
      }
    })

    return () => {
      isActive.current = false
    }
  }, [dispatch, isPaid, loadContract])

  // Po załadowaniu bierzemy grupę ze store, bo podpisywanie (np. zmiana numeru telefonu) może ją podmienić
  const group = contractState.isLoaded ? contractGroups?.[0] : undefined
  const contract = {
    ...contractState,
    pdf: group?.pdf || '',
    groupId: group?.id || '',
    group
  }

  return {
    mode,
    isLoading,
    needsContract,
    contract,
    canProceed: !needsContract || (!contract.isLoading && !!contract.pdf),
    retryContract: loadContract,
    // contract_accepted bierze się z terms.rentalTerms w sendBasket; przy niezmienionym koszyku
    // backend zwraca tę samą umowę, więc podgląd się nie zmienia.
    acceptContract: requestContract
  }
}

export default useCheckout
